import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Avatar, Button, Drawer, useMediaQuery, useTheme } from "@mui/material";
import { IoAdd, IoChevronBack, IoChevronForward, IoLogOut, IoGrid, IoBook, IoClose } from "react-icons/io5";
import { useChatThreads } from "../../hooks";
import ChatSidebarItem from "./ChatSidebarItem";
import { adminColors, TOUCH_HIT_SX, FOCUS_RING_ON_DARK, AVATAR_RADIUS } from "../../theme/adminTheme";
import { isStaffRole, ROLE_LABELS } from "../../constants/roles";
import { getInitials } from "../../utils/userDisplay";

const sidebarGradient = `linear-gradient(180deg, ${adminColors.sidebar} 0%, #0e0f13 100%)`;
const avatarGradient = `linear-gradient(135deg, ${adminColors.accent}, ${adminColors.accentDark})`;

// Width of the mobile overlay drawer: fills most of a phone screen while
// leaving a 15vw margin so MUI's dimmed backdrop (the scrim) stays visible
// at the edge, signaling this is a temporary overlay above the chat rather
// than a full-screen view — it never exceeds the desktop sidebar's own
// width either.
const MOBILE_DRAWER_WIDTH = "min(85vw, 320px)";

const sidebarActionBtnSx = {
  flex: 1,
  color: "rgba(255,255,255,0.75)",
  borderColor: adminColors.sidebarBorder,
  textTransform: "none",
  fontSize: "0.8rem",
  py: 0.5,
  "&:hover": {
    borderColor: "rgba(255,255,255,0.3)",
    backgroundColor: "rgba(255,255,255,0.06)",
    color: "white",
  },
};

// Rests at the same muted weight as sidebarActionBtnSx (its neighbour) —
// red is reserved for hover/focus-visible. Permanently visible now (no
// longer a hover-only reveal), a resting red would out-compete Dashboard/
// Guide beside it and read as more urgent than a reversible, non-
// destructive action warrants.
const sidebarSignOutBtnSx = {
  flex: 1,
  color: "rgba(255,255,255,0.75)",
  borderColor: adminColors.sidebarBorder,
  textTransform: "none",
  fontSize: "0.8rem",
  py: 0.5,
  "&:hover": {
    color: "#f87171",
    borderColor: "#f87171",
    backgroundColor: "rgba(248,113,113,0.08)",
  },
  "&:focus-visible": {
    color: "#f87171",
    borderColor: "#f87171",
    backgroundColor: "rgba(248,113,113,0.08)",
  },
};

const groupThreads = (threads) => {
  const now = Date.now();
  const groups = { Today: [], "Previous 7 days": [], Older: [] };
  threads.forEach((t) => {
    const updated = new Date(t.updated_at || t.created_at).getTime();
    const diffDays = (now - updated) / 86400000;
    if (diffDays < 1) groups.Today.push(t);
    else if (diffDays < 7) groups["Previous 7 days"].push(t);
    else groups.Older.push(t);
  });
  return groups;
};

// mobileOpen/onMobileClose control the overlay Drawer used below the md
// breakpoint; they're ignored at md+ where the sidebar is the persistent
// column it's always been.
const ChatSidebar = ({ activeConversationId, onSelectThread, showMessage, username, userRole, onLogout, mobileOpen = false, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const { threads, createThread, renameThread, deleteThread } = useChatThreads({ showMessage });
  const isAdmin = isStaffRole(userRole);

  // Selecting a thread from the mobile overlay should also dismiss it -
  // the whole point of the drawer is to get out of the way once its job
  // (picking a thread) is done.
  const handleSelectThread = (threadId) => {
    onSelectThread(threadId);
    if (isMobile) onMobileClose?.();
  };

  const handleLogoutClick = () => {
    setUserMenuAnchor(null);
    onLogout();
  };

  const handleDashboardClick = () => {
    setUserMenuAnchor(null);
    navigate("/admin");
  };

  const handleStudentGuideClick = () => {
    setUserMenuAnchor(null);
    window.open('/guides/student-guide.html', '_blank');
  };

  // useChatThreads already catches its own errors, shows the user feedback,
  // and returns null/false on failure - it never rethrows - so there is no
  // error path left for a try/catch here to handle.
  const handleNewChat = async () => {
    const thread = await createThread();
    if (thread) handleSelectThread(thread.id);
  };

  const handleDelete = async (threadId) => {
    const wasActive = threadId === activeConversationId;
    const ok = await deleteThread(threadId);
    if (ok && wasActive) {
      onSelectThread(null); // caller navigates to /chat, which resolves/creates a thread
    }
  };

  // Collapsed rail has no room for the inline hover actions, so it falls
  // back to a small dropdown menu with the same role-based options.
  const userMenu = (
    <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}>
      {isAdmin ? (
        <MenuItem onClick={handleDashboardClick}>
          <ListItemIcon>
            <IoGrid size={16} />
          </ListItemIcon>
          <ListItemText>Dashboard</ListItemText>
        </MenuItem>
      ) : (
        <MenuItem onClick={handleStudentGuideClick}>
          <ListItemIcon>
            <IoBook size={16} />
          </ListItemIcon>
          <ListItemText>Student Guide</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={handleLogoutClick}>
        <ListItemIcon>
          <IoLogOut size={16} />
        </ListItemIcon>
        <ListItemText>Logout</ListItemText>
      </MenuItem>
    </Menu>
  );

  // The collapsed rail is a desktop-only affordance (a manual "give me back
  // some width" toggle). On mobile the sidebar is an overlay that's either
  // fully open or fully closed, so collapsed state never applies there.
  if (collapsed && !isMobile) {
    return (
      <Box
        sx={{
          width: 48,
          flexShrink: 0,
          background: sidebarGradient,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100%",
          pt: 1.5,
        }}
      >
        <Tooltip title="Expand sidebar" placement="right">
          <IconButton size="small" onClick={() => setCollapsed(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" }, ...TOUCH_HIT_SX }}>
            <IoChevronForward size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="New chat" placement="right">
          <IconButton size="small" onClick={handleNewChat} sx={{ color: "rgba(255,255,255,0.7)", mt: 1, "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" }, ...TOUCH_HIT_SX }}>
            <IoAdd size={18} />
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        <Tooltip title={username || "Account"} placement="right">
          {/* Outer box is the 40x40 hit target (WCAG touch-target minimum);
              negative top/bottom margins compensate so it doesn't shift
              surrounding layout, and the visible 28px avatar is centered
              inside via the nested Box. */}
          <Box
            role="button"
            tabIndex={0}
            aria-label={`Account menu for ${username || "Account"}`}
            aria-haspopup="true"
            aria-expanded={Boolean(userMenuAnchor)}
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setUserMenuAnchor(e.currentTarget);
              }
            }}
            sx={{
              width: 40,
              height: 40,
              mt: -0.75,
              mb: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              cursor: "pointer",
              "&:focus-visible": {
                outline: `2px solid ${FOCUS_RING_ON_DARK}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                // Matches the real <Avatar>'s shape (the account footer
                // below, and every other avatar in the app) via the theme's
                // shared AVATAR_RADIUS token — not a circle, so the avatar
                // doesn't visibly morph shape when the sidebar collapses.
                borderRadius: AVATAR_RADIUS,
                background: avatarGradient,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: 700,
              }}
            >
              {getInitials(username)}
            </Box>
          </Box>
        </Tooltip>
        {userMenu}
      </Box>
    );
  }

  const groups = groupThreads(threads);

  const sidebarContent = (
    <Box
      sx={{
        width: isMobile ? "100%" : 280,
        flexShrink: 0,
        background: sidebarGradient,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        color: "white",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, pt: 1.5, pb: 1 }}>
        <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.03em" }}>
          Chats
        </Typography>
        {/* The collapse toggle only makes sense for the persistent desktop
            column. The mobile drawer instead gets an explicit close button
            (below) - relying on the scrim tap / Esc alone isn't discoverable
            as a way to dismiss it. */}
        {isMobile ? (
          <IconButton
            size="small"
            onClick={onMobileClose}
            aria-label="Close chat list"
            sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", color: "white" }, ...TOUCH_HIT_SX }}
          >
            <IoClose size={18} />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={() => setCollapsed(true)} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", color: "white" }, ...TOUCH_HIT_SX }} aria-label="Collapse sidebar">
            <IoChevronBack size={16} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-label="New chat"
          onClick={handleNewChat}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleNewChat();
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            minHeight: 44,
            borderRadius: "8px",
            border: `1px solid ${adminColors.sidebarBorder}`,
            cursor: "pointer",
            color: "rgba(255,255,255,0.85)",
            fontSize: "13.5px",
            fontWeight: 500,
            transition: "background-color 0.15s ease, border-color 0.15s ease",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.3)" },
            "&:focus-visible": {
              outline: `2px solid ${FOCUS_RING_ON_DARK}`,
              outlineOffset: 2,
            },
          }}
        >
          <IoAdd size={16} />
          New chat
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 1.25, pb: 1 }}>
        {Object.entries(groups).map(([label, items]) =>
          items.length === 0 ? null : (
            <Box key={label} sx={{ mb: 1 }}>
              <Typography sx={{ px: 1, pb: 0.5, fontSize: "11px", fontWeight: 500, color: "rgba(255,255,255,0.4)" }}>
                {label}
              </Typography>
              {items.map((thread) => (
                <ChatSidebarItem
                  key={thread.id}
                  thread={thread}
                  isActive={thread.id === activeConversationId}
                  onSelect={handleSelectThread}
                  onRename={renameThread}
                  onDelete={handleDelete}
                />
              ))}
            </Box>
          )
        )}
      </Box>

      <Box
        sx={{
          p: 1.5,
          borderTop: `1px solid ${adminColors.sidebarBorder}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: avatarGradient,
              fontSize: "0.8rem",
              fontWeight: 700,
              mr: 1.5,
            }}
          >
            {getInitials(username)}
          </Avatar>
          <Box sx={{ overflow: "hidden" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "white", lineHeight: 1.3 }} noWrap>
              {username || "Account"}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", lineHeight: 1.2 }} noWrap>
              {isAdmin ? (ROLE_LABELS[userRole] || userRole || "Admin") : "Student"}
            </Typography>
          </Box>
        </Box>
        {/* Always-on account actions - previously revealed on hover/focus,
            now a permanent part of the footer's designed layout on every
            breakpoint (not just mobile). */}
        <Box
          className="sidebar-actions"
          sx={{
            display: "flex",
            gap: 1,
            marginTop: "12px",
          }}
        >
          {isAdmin ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<IoGrid size={14} />}
              onClick={handleDashboardClick}
              sx={{ ...sidebarActionBtnSx, ...TOUCH_HIT_SX }}
            >
              Dashboard
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<IoBook size={14} />}
              onClick={handleStudentGuideClick}
              sx={{ ...sidebarActionBtnSx, ...TOUCH_HIT_SX }}
            >
              Guide
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<IoLogOut size={14} />}
            onClick={handleLogoutClick}
            sx={{ ...sidebarSignOutBtnSx, ...TOUCH_HIT_SX }}
          >
            Sign Out
          </Button>
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        transitionDuration={prefersReducedMotion ? 0 : undefined}
        slotProps={{
          paper: {
            sx: {
              width: MOBILE_DRAWER_WIDTH,
              border: "none",
              backgroundImage: "none",
            },
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return sidebarContent;
};

export default ChatSidebar;
